import React from 'react';

import Link from '@dashboard/components/mui-atoms/Link';
import { PlatformSendingStatus } from '@dashboard/generated/wp/authentication';
import { WaypointFeatureTier } from '@dashboard/generated/wp/platform';
import useCurrentWpPlatform from '@dashboard/hooks/useCurrentWpPlatform';
import { Box, Typography } from '@mui/material';
import zIndex from '@mui/material/styles/zIndex';
import TooltipUnderline from '@wp-react/components/TooltipUnderline';

export function usePlatformSendingStatusAlertPresent() {
  const { data } = useCurrentWpPlatform();
  const platform = data?.Platform;
  if (!platform) {
    return false;
  }

  switch (platform.sendingStatus) {
    case PlatformSendingStatus.Disabled:
    case PlatformSendingStatus.Sample:
    case PlatformSendingStatus.TestMode:
    case PlatformSendingStatus.VerificationNeeded:
    case PlatformSendingStatus.ApprovalNeeded:
    case PlatformSendingStatus.SubscriptionNeeded:
      return true;
    case PlatformSendingStatus.LiveMode:
    default:
      return false;
  }
}

export default function PlatformSendingStatusAlert() {
  const { data } = useCurrentWpPlatform();

  const platform = data?.Platform;
  if (!platform) {
    return null;
  }

  const testModeMessage = () => {
    if (platform.activeWaypointTier === WaypointFeatureTier.Free) {
      return (
        <>
          This workspace is on a{' '}
          <Typography variant="inherit" fontWeight="medium" component="span">
            <TooltipUnderline title="Emails sent while on test mode will only be delivered to teammates that have joined the workspace.">
              <Link
                color="inherit"
                href="https://www.usewaypoint.com/docs/trial-plan-limitations"
                target="_blank"
                underline="none"
              >
                free trial plan
              </Link>
            </TooltipUnderline>
          </Typography>
          .
        </>
      );
    }
    return null;
  };

  switch (platform.sendingStatus) {
    case PlatformSendingStatus.Sample:
      return (
        <GlobalAlert color="text.primary" bgcolor="brand.brown">
          This is a sample workspace. Whenever you&rsquo;re ready{' '}
          <Link href="/organizations/new" color="inherit" underline="always">
            create your own workspace
          </Link>
          .
        </GlobalAlert>
      );
    case PlatformSendingStatus.Disabled:
      return (
        <GlobalAlert color="white" bgcolor="brand.red">
          This workspace is{' '}
          <Link
            href="https://www.usewaypoint.com/docs/under-review#suspended"
            target="_blank"
            color="inherit"
            underline="always"
          >
            suspended
          </Link>{' '}
          and sending has been paused.{' '}
          <Link href="http://www.usewaypoint.com/help" target="_blank" color="inherit" underline="always">
            Contact support
          </Link>{' '}
          for details.
        </GlobalAlert>
      );
    case PlatformSendingStatus.TestMode:
    case PlatformSendingStatus.VerificationNeeded:
      return (
        <GlobalAlert color="text.primary" bgcolor="brand.brown">
          <>{testModeMessage()}</> Next step:{' '}
          <Link
            href="https://www.usewaypoint.com/docs/verified-domains"
            target="_blank"
            color="inherit"
            underline="always"
            fontWeight="medium"
          >
            add a verified domain
          </Link>
          .
        </GlobalAlert>
      );
    case PlatformSendingStatus.ApprovalNeeded:
      return (
        <GlobalAlert color="text.primary" bgcolor="brand.brown">
          <>{testModeMessage()} Your verified domain is </>
          <Typography variant="inherit" fontWeight="medium" component="span">
            <TooltipUnderline title="Typically reviewed by Waypoint within 24 hours.">
              awaiting approval
            </TooltipUnderline>
          </Typography>
          .
        </GlobalAlert>
      );
    case PlatformSendingStatus.SubscriptionNeeded:
      return (
        <GlobalAlert color="text.primary" bgcolor="brand.brown">
          <>{testModeMessage()} </>
          <Link href="https://www.usewaypoint.com/docs/manage-plan" target="_blank" color="inherit" underline="always">
            Upgrade
          </Link>
          <> to start sending live emails.</>
        </GlobalAlert>
      );
    case PlatformSendingStatus.LiveMode:
    default:
      return null;
  }
}

type TNode = JSX.Element | string;
type Props = {
  color: Parameters<typeof Box>[0]['color'];
  bgcolor: Parameters<typeof Box>[0]['bgcolor'];
  children: TNode | TNode[];
};
function GlobalAlert({ children, color, bgcolor }: Props) {
  return (
    <Box
      color={color}
      bgcolor={bgcolor}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 0.5,
        height: { xs: 'auto', md: 32 },
        position: 'relative',
        zIndex: zIndex.appBar,
      }}
    >
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}
